import { useEffect, useMemo, useRef, useState } from "react";
import type { ReviewComment, ReviewReply, ReviewStatus } from "../../contracts/types";
import { addRemoteComment, addRemoteReply, attachToReview, deleteRemoteComment, detachFromReview, getReview, setRemoteStatus, submitReceipt, type RemoteReview } from "../reviewApi";
import { ReviewPanel } from "./ReviewPanel";
import { ContractPreview } from "./ContractPreview";
import { ShareButtons } from "./ShareButtons";
import SignaturePad from "./SignaturePad";
import { FILE_ACCEPT, fileSize, fileUrl, uploadFile } from "../filesApi";

// Standalone live-review view for anyone opening a /?review=<id> link. Reads and
// writes the shared Durable Object, polling every few seconds so all parties on
// the same link see each other's comments.
export function SharedReviewView({ id }: { id: string }) {
  const [data, setData] = useState<RemoteReview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const versionRef = useRef(-1);

  async function refresh() {
    try {
      const d = await getReview(id);
      if (d.version !== versionRef.current) {
        versionRef.current = d.version;
        setData(d);
      }
      setErr(null);
    } catch (e: any) {
      setErr(String(e.message).slice(0, 120));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Brand the browser tab to the project, not the app.
  useEffect(() => {
    if (data?.meta.title) document.title = `${data.meta.title} — Discussion Terms`;
    else document.title = "Discussion Terms";
  }, [data?.meta.title]);

  function apply(d: RemoteReview) {
    versionRef.current = d.version;
    setData(d);
  }
  const onAdd = async (c: ReviewComment) => apply(await addRemoteComment(id, c));
  const onReply = async (cid: string, r: ReviewReply) => apply(await addRemoteReply(id, cid, r));
  const onStatus = async (cid: string, s: ReviewStatus) => apply(await setRemoteStatus(id, cid, s));
  const onRemove = async (cid: string) => apply(await deleteRemoteComment(id, cid));

  // Recipient uploads (an invoice / supporting document against this document).
  const fileRef = useRef<HTMLInputElement>(null);
  const [attaching, setAttaching] = useState(false);
  const [attachErr, setAttachErr] = useState("");
  const [previewId, setPreviewId] = useState<string | null>(null);
  async function onAttach(f: File | undefined) {
    if (!f) return;
    setAttachErr("");
    setAttaching(true);
    try {
      const up = await uploadFile(f);
      apply(await attachToReview(id, { id: up.id, name: up.name, size: up.size, at: new Date().toISOString() }));
    } catch (e: any) {
      setAttachErr(e?.message || "Upload failed.");
    } finally {
      setAttaching(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }
  async function onDetach(attachmentId: string) {
    setAttachErr("");
    try {
      apply(await detachFromReview(id, attachmentId));
    } catch (e: any) {
      setAttachErr(e?.message || "Could not remove the file.");
    }
  }

  // Receipt confirmation (shown when the sender asks the payee to confirm receipt).
  const receiptFileRef = useRef<HTMLInputElement>(null);
  const [rcName, setRcName] = useState("");
  const [rcSig, setRcSig] = useState<string | null>(null);
  const [rcPhoto, setRcPhoto] = useState<{ id: string; name: string; size: number; at: string } | null>(null);
  const [rcBusy, setRcBusy] = useState(false);
  const [rcErr, setRcErr] = useState("");
  async function onReceiptPhoto(f: File | undefined) {
    if (!f) return;
    setRcErr("");
    setRcBusy(true);
    try {
      const up = await uploadFile(f);
      setRcPhoto({ id: up.id, name: up.name, size: up.size, at: new Date().toISOString() });
    } catch (e: any) {
      setRcErr(e?.message || "Could not upload the photo.");
    } finally {
      setRcBusy(false);
      if (receiptFileRef.current) receiptFileRef.current.value = "";
    }
  }
  async function confirmReceipt() {
    if (!rcName.trim() || !rcSig) { setRcErr("Enter your name and sign above."); return; }
    setRcErr("");
    setRcBusy(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      apply(await submitReceipt(id, { confirmedBy: rcName.trim(), signature: rcSig, confirmedDate: today, receiptPhoto: rcPhoto || undefined, at: new Date().toISOString() }));
    } catch (e: any) {
      setRcErr(e?.message || "Could not submit the confirmation.");
    } finally {
      setRcBusy(false);
    }
  }
  async function redoReceipt() {
    setRcErr("");
    try {
      apply(await submitReceipt(id, null));
      setRcName(""); setRcSig(null); setRcPhoto(null);
    } catch (e: any) {
      setRcErr(e?.message || "Could not reset.");
    }
  }

  const anchors = useMemo(
    () => [{ value: "__whole__", label: "General / whole contract" }, ...((data?.contract?.parts || []).map((p) => ({ value: p.id, label: p.name })))],
    [data?.contract]
  );

  if (loading && !data) {
    return <div className="shared-loading"><span className="spinner dark-spin" /> Loading discussion…</div>;
  }
  if (data && !data.exists) {
    return <div className="shared-loading">This discussion link was not found. Ask the sender for a current link.</div>;
  }

  const projectTitle = data?.meta.title || "Project";

  return (
    <div className="app">
      <div className="pane form">
        <div className="topbar">
          <div className="brand" style={{ lineHeight: 1.1 }}>
            {projectTitle}
            <div style={{ fontSize: 11, color: "var(--muted)", fontFamily: "Inter, sans-serif", fontWeight: 400, letterSpacing: 1, textTransform: "uppercase" }}>Discussion Terms</div>
          </div>
          <div className="spacer" />
          {err && <span className="hint" style={{ color: "#a3402f", margin: 0 }}>offline — retrying…</span>}
        </div>
        <div className="content">
          <div className="notice">Discussion of <b>{projectTitle}</b>{data?.meta.ref ? ` (${data.meta.ref})` : ""}. Comments are shared with everyone who has this link and update automatically.</div>

          {/* Receipt confirmation — shown when the sender asks the payee to confirm
              they received the payment (sign + upload a photo of the receipt). */}
          {data?.requestReceipt && (
            data.receipt ? (
              <div className="receipt-box done">
                <div className="lbl">✓ Receipt of payment confirmed</div>
                <div className="receipt-summary">
                  <div>Confirmed by <b>{data.receipt.confirmedBy || "—"}</b>{data.receipt.confirmedDate ? ` on ${data.receipt.confirmedDate}` : ""}.</div>
                  {data.receipt.signature && <img className="receipt-sig" src={data.receipt.signature} alt="signature" />}
                  {data.receipt.receiptPhoto && (
                    <a className="fattach-file" href={fileUrl(data.receipt.receiptPhoto.id)} target="_blank" rel="noreferrer">📎 {data.receipt.receiptPhoto.name} <span className="muted fattach-size">{fileSize(data.receipt.receiptPhoto.size)}</span></a>
                  )}
                </div>
                <button className="link" onClick={redoReceipt}>Redo confirmation</button>
              </div>
            ) : (
              <div className="receipt-box">
                <div className="lbl">Confirm receipt of payment</div>
                <div className="hint" style={{ margin: "0 0 10px" }}>Please confirm you have received the payment for this certificate: enter your name, sign below, and upload a photo of the receipt.</div>
                <label><span className="lbl">Your name</span><input value={rcName} onChange={(e) => setRcName(e.target.value)} placeholder="Name of person confirming" /></label>
                <div className="lbl" style={{ marginTop: 8 }}>Signature</div>
                <SignaturePad onChange={setRcSig} />
                <div className="lbl" style={{ marginTop: 8 }}>Photo of the receipt <span className="lbl-opt">(optional)</span></div>
                <input ref={receiptFileRef} type="file" accept={FILE_ACCEPT} style={{ display: "none" }} onChange={(e) => onReceiptPhoto(e.target.files?.[0])} />
                {rcPhoto ? (
                  <div className="attach-item"><span className="fattach-file">📎 {rcPhoto.name}</span><span className="muted fattach-size">{fileSize(rcPhoto.size)}</span><button className="chip-x" onClick={() => setRcPhoto(null)}>✕</button></div>
                ) : (
                  <button className="mini" onClick={() => receiptFileRef.current?.click()} disabled={rcBusy}>{rcBusy ? <span className="spinner" /> : "⤒ Upload receipt photo"}</button>
                )}
                <div style={{ marginTop: 12 }}>
                  <button className="dark" onClick={confirmReceipt} disabled={rcBusy || !rcName.trim() || !rcSig}>{rcBusy ? <span className="spinner" /> : "✓ Confirm receipt of payment"}</button>
                </div>
                {rcErr && <div className="hint" style={{ color: "#a3402f", marginTop: 6 }}>{rcErr}</div>}
              </div>
            )
          )}

          {/* Documents on this link: the certificate (previewed on the right) and
              the invoice / supporting files attached to it — by the issuer when
              sharing, or by the contractor here. Everyone on the link sees both. */}
          <div className="share-box attach-box">
            <div className="lbl">Documents on this link</div>
            <div className="attach-list">
              <div className="attach-item">
                <span className="fattach-file">📄 {data?.contract?.title || projectTitle}</span>
                <span className="muted fattach-size">shown on the right</span>
              </div>
              {(data?.attachments || []).map((a) => (
                <div className="attach-item-wrap" key={a.id}>
                  <div className="attach-item">
                    <a className="fattach-file" href={fileUrl(a.id)} target="_blank" rel="noreferrer" title={`Open ${a.name}`}>📎 {a.note ? `${a.note}: ` : ""}{a.name}</a>
                    <span className="muted fattach-size">{fileSize(a.size)}</span>
                    <button className="mini" onClick={() => setPreviewId(previewId === a.id ? null : a.id)}>{previewId === a.id ? "Hide" : "View"}</button>
                    <a className="mini btnlink" href={fileUrl(a.id)} download={a.name} title="Download">⤓</a>
                    <button className="chip-x" onClick={() => onDetach(a.id)} title="Remove this attachment">✕</button>
                  </div>
                  {previewId === a.id && (
                    <div className="attach-preview">
                      {/\.(png|jpe?g|webp|gif)$/i.test(a.name) ? <img src={fileUrl(a.id)} alt={a.name} /> : <iframe src={fileUrl(a.id)} title={a.name} />}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="hint" style={{ margin: "8px 0 6px" }}>{(data?.attachments || []).length ? "Add another supporting document (PDF or image):" : "Contractor: attach your invoice or payment statement for this certificate (PDF or image). It is shared with everyone on this link."}</div>
            <input ref={fileRef} type="file" accept={FILE_ACCEPT} style={{ display: "none" }} onChange={(e) => onAttach(e.target.files?.[0])} />
            <button className={(data?.attachments || []).length ? "mini" : "dark sm"} onClick={() => fileRef.current?.click()} disabled={attaching}>{attaching ? <span className="spinner" /> : (data?.attachments || []).length ? "⤒ Attach a file" : "⤒ Attach invoice"}</button>
            {attachErr && <span className="fattach-err" style={{ marginLeft: 8 }}>{attachErr}</span>}
          </div>

          <div className="share-box">
            <div className="lbl">Send this link on — certificate and attachments together</div>
            <div className="share-row">
              <ShareButtons url={typeof window !== "undefined" ? window.location.href : ""} title={data?.meta.title} />
              <button onClick={() => navigator.clipboard?.writeText(window.location.href)}>Copy link</button>
            </div>
          </div>

          <ReviewPanel
            comments={data?.comments || []}
            anchors={anchors}
            onAdd={onAdd}
            onReply={onReply}
            onStatus={onStatus}
            onRemove={onRemove}
          />
        </div>
      </div>
      <div className="pane preview">
        {data?.contract ? <ContractPreview contract={data.contract} /> : <div className="shared-loading">No document attached to this review.</div>}
      </div>
    </div>
  );
}
